import styled from '@emotion/styled'
import {
    pnVideoClassname,
    videoClassname,
} from '../../../content/Video/Video.styled'
import { colors } from '../../../__styling/settings/colors'
import { fonts } from '../../../__styling/settings/fonts'
import { zIndex } from '../../../__styling/settings/z-index'
import { breakpoint } from '../../../__styling/style-functions'
import { calcRem } from '../../../__styling/style-functions/calc-rem'
import { hexToRGBA } from '../../../__styling/style-mixins'
import { themedValue } from '../../../__styling/themed-value'
import { PNArticleCardTheme } from '../../../nextgen-news/perthnow/cards/PNCard.Theme'
import { pnDismissControlClassName } from '../../../nextgen-news/perthnow/publication/PNVideoSkin/PNVideoSkin'
import { metricsPN, colorsPN } from '@news-mono/design-tokens'

const iconPlay = require('../assets/play.svg')
const iconPause = require('../assets/pause.svg')
const iconVolumeX = require('../assets/volumex.svg')
const iconVolume0 = require('../assets/volume.svg')
const iconVolume1 = require('../assets/volume1.svg')
const iconVolume2 = require('../assets/volume2.svg')
const iconMaximize = require('../assets/maximize.svg')
const iconMinimize = require('../assets/minimize.svg')

const panelHeightMobile = 40
const panelHeightTablet = 52
const panelHeightDesktop = 52

const { spacing } = metricsPN
const { text, fill } = colorsPN

export const StyledBrightcoveVideo = styled('video')(({ theme }) => ({
    // the video classhame is added before the contextual selector to ensure specificity in ie10
    [`.${videoClassname} &`]: [
        {
            position: 'absolute',
            top: 0,
            bottom: 0,
            right: 0,
            left: 0,

            display: 'block',
            verticalAlign: 'top',
            boxSizing: 'border-box',
            color: colors.white,
            background: colors.black,
            // needed to override brightcove player css
            width: 'auto',
            height: 'auto',

            // video before brightcove initialised on element
            '&:not(.vjs-controls-enabled)': {
                width: '100%',
                height: '100%',
                position: 'absolute',
            },

            // video poster image sizing
            '& .vjs-poster': {
                backgroundSize: 'cover',
            },

            // video before brightcove initialised on element
            'video&': {
                display: 'none',
            },

            // brightcove rewrites the dom to move the video down a level
            // .vjs-tech is to add specicifity
            '& video.vjs-tech': {
                width: '100%',
                height: '100%', // vertical videos need 100% height to avoid letterboxing
                minHeight: '140px',
                position: 'absolute',
            },

            // custom control bar
            '& .vjs-control-bar': [
                {
                    /*
                     * Debugger / styling helper, pins control bar to the bottom
                     * of the video element so you can style without hovering constantly
                     */
                    // transform: `translateY(0) !important`,

                    height: calcRem(45),
                    bottom: '-1px',
                    zIndex: zIndex.common.videoControlBar,
                    color: themedValue(theme, {
                        perthnow: colors.white,
                        fallback: undefined,
                    }),
                    background: themedValue(theme, {
                        sevennews: hexToRGBA(colors.black, 0.7),
                        perthnow: colors.perthnow.greyBasalt90,
                        fallback: undefined,
                    }),
                },
                theme.kind === 'sevennews' && {
                    fontFamily: fonts.sevennews.sansSerifCond,
                    fontWeight: 300,
                    padding: calcRem(0, 20),
                },
                theme.kind === 'thenightly' && {
                    fontFamily: fonts.thenightly.sansSerif,
                },
            ],
            '& .vjs-menu': {
                zIndex: zIndex.common.videoContextMenu,
            },

            // fighting brightcove for specificity here - use this to show control bar on hover
            ['&.not-hover.vjs-has-started.vjs-paused.vjs-user-active .vjs-control-bar, ' +
            '&.vjs-has-started.vjs-paused.vjs-user-inactive .vjs-control-bar, ' +
            '&.not-hover.vjs-has-started.vjs-playing.vjs-user-active .vjs-control-bar, ' +
            '&.vjs-has-started.vjs-playing.vjs-user-inactive .vjs-control-bar, ' +
            '&.vjs-has-started.vjs-playing.vjs-user-inactive .vjs-control-bar']:
                {
                    // Using rems directly as this needs an exact rem value
                    transform: `translateY(${calcRem(44)})`,
                },

            // override the lineheight on ie11
            '& .vjs-button > .vjs-icon-placeholder::before': {
                lineHeight: '2',
            },

            '& .vjs-control.vjs-button': {
                outline: 'none',
            },

            '& .vjs-control.vjs-button:hover, & .vjs-control:hover .vjs-button':
                {
                    textShadow: `0 0 1em ${colors.white}`,
                },

            ['& .vjs-play-control .vjs-icon-placeholder::before, ' +
            '& .vjs-mute-control .vjs-icon-placeholder::before, ' +
            '& .vjs-fullscreen-control .vjs-icon-placeholder::before']: {
                // sets the size of the icons and how they align in the controls box
                fontSize: calcRem(22),
                width: calcRem(45),
            },

            '& .vjs-live-display': {
                fontSize: calcRem(15),
                padding: calcRem(7, 22),
                fontWeight: 500,
            },

            '& .vjs-fullscreen-control .vjs-icon-placeholder::before': {
                fontSize: calcRem(25),
            },

            '& .vjs-volume-menu-button-horizontal:hover': {
                '.vjs-menu .vjs-menu-content': {
                    width: calcRem(49),

                    [breakpoint('sm')]: {
                        width: calcRem(59),
                    },
                },
            },

            '& .vjs-volume-bar.vjs-slider-horizontal': {
                margin: `${calcRem(21)} 0 ${calcRem(20)}`,
                width: calcRem(49),
                height: calcRem(4),
                background: themedValue(theme, {
                    sevennews: colors.sevennews.midGray,
                    fallback: undefined,
                }),
                [breakpoint('sm')]: {
                    width: calcRem(59),
                },
            },
            '& .vjs-volume-bar.vjs-slider-horizontal:hover .vjs-menu': {
                width: calcRem(49),

                [breakpoint('sm')]: {
                    width: calcRem(59),
                },
            },

            '& .vjs-menu-button-inline': {
                '&:hover, &:focus': {
                    width: calcRem(94),

                    [breakpoint('sm')]: {
                        width: calcRem(104),
                    },
                },
            },

            '& .vjs-menu-button-inline .vjs-menu': {
                left: calcRem(45),
                width: calcRem(49),

                [breakpoint('sm')]: {
                    width: calcRem(59),
                },
            },

            '& .vjs-menu-button-inline.vjs-slider-active': {
                width: calcRem(94),

                [breakpoint('sm')]: {
                    width: calcRem(104),
                },
            },

            '& .vjs-slider-horizontal .vjs-volume-level': {
                height: calcRem(4),
            },

            '& .vjs-time-control': {
                lineHeight: calcRem(46),
                fontSize: calcRem(16),
                padding: 0,
                minWidth: calcRem(32),
                fontWeight: 700,

                color: themedValue(theme, {
                    perthnow: colors.white,
                    fallback: undefined,
                }),
            },

            '& .vjs-time-control.vjs-time-divider': {
                minWidth: 0,
            },

            // See https://github.com/videojs/video.js/blob/master/docs/guides/text-tracks.md#text-track-settings & https://github.com/videojs/video.js/issues/3202
            '& .vjs-texttrack-settings': {
                display: 'none',
            },

            '& .vjs-play-progress, .vjs-volume-level': {
                background: themedValue(theme, {
                    perthnow: colors.perthnow.pinkThulite,
                    sevennews: (resolved) => resolved.video.primaryColor,
                    thenightly: colors.thenightly.neutral[0],
                    fallback: undefined,
                }),
            },
        },
        theme.kind === 'sevennews' && {
            '&.vjs-ad-playing .vjs-play-progress': {
                backgroundColor: colors.sevennews.turbo,
            },

            '& .vjs-slider': {
                background: 'none',
            },

            '& .vjs-load-progress': {
                background: 'none',
            },

            '& .vjs-load-progress div': {
                background: colors.white30,
            },

            '& .vjs-progress-control:hover': {
                paddingTop: 0,
            },

            ['& .vjs-fullscreen-control, ' +
            '& .vjs-volume-panel, ' +
            '& .vjs-play-control']: {
                width: '5em',
            },

            ['.vjs-fullscreen-control .vjs-icon-placeholder::before, ' +
            '.vjs-subs-caps-button .vjs-icon-placeholder::before, ' +
            '.vjs-play-control .vjs-icon-placeholder::before, ' +
            '.vjs-mute-control .vjs-icon-placeholder::before']: {
                fontSize: '1.625rem',
                lineHeight: 1.7,
            },

            '& .vjs-time-control': {
                fontSize: calcRem(15),
                fontWeight: 500,
                padding: calcRem(0, 8),
            },

            '& .vjs-progress-control .vjs-mouse-display': {
                cursor: 'pointer',
            },
        },
        theme.kind === 'thenightly' && {
            '&.vjs-ad-playing .vjs-play-progress': {
                backgroundColor: colors.thenightly.video.adPlaying,
            },
            '& .vjs-time-control': {
                fontWeight: 400,
            },
        },
    ],

    [`.${pnVideoClassname} &`]: [
        {
            position: 'absolute',
            top: 0,
            bottom: 0,
            right: 0,
            left: 0,

            display: 'block',
            verticalAlign: 'top',
            boxSizing: 'border-box',
            color: colors.white,
            background: 'transparent',
            // needed to override brightcove player css
            width: 'auto',
            height: 'auto',

            // video before brightcove initialised on element
            '&:not(.vjs-controls-enabled)': {
                width: '100%',
                height: '100%',
                position: 'absolute',
            },

            // video poster image sizing
            '& .vjs-poster': {
                backgroundSize: 'cover',
            },

            // video before brightcove initialised on element
            'video&': {
                display: 'none',
            },

            // brightcove rewrites the dom to move the video down a level
            // .vjs-tech is to add specicifity
            '& video.vjs-tech': {
                width: '100%',
                height: 'auto',
                minHeight: '140px',
                position: 'absolute',
            },

            // custom control bar
            '& .vjs-control-bar': [
                {
                    background:
                        'linear-gradient(180deg, rgba(0, 0, 0, 0.24) -8.06%, rgba(0, 0, 0, 0.8) 74.07%);',
                    height: calcRem(panelHeightMobile),
                    bottom: calcRem(-1),
                    zIndex: zIndex.common.videoControlBar,
                    color: colors.white,
                    padding: calcRem(0, spacing['S']),

                    '& .vjs-current-time, & .vjs-time-divider, & .vjs-duration ':
                        {
                            ...PNArticleCardTheme['3XS'].tag,
                        },
                    '& .vjs-control .vjs-icon-placeholder': {
                        '&:before': {
                            lineHeight: calcRem(panelHeightMobile),
                        },
                    },
                    [breakpoint('sm')]: {
                        height: calcRem(panelHeightTablet),
                        padding: calcRem(0, spacing['XS']),
                        '& .vjs-control .vjs-icon-placeholder': {
                            '&:before': {
                                lineHeight: calcRem(panelHeightTablet),
                            },
                        },
                    },
                    [breakpoint('lg')]: {
                        height: calcRem(panelHeightDesktop),
                        '& .vjs-control .vjs-icon-placeholder': {
                            '&:before': {
                                lineHeight: calcRem(panelHeightDesktop),
                            },
                        },
                    },
                },
            ],
            '& .vjs-menu': {
                zIndex: zIndex.common.videoContextMenu,
            },

            ['& .vjs-volume-panel, & .vjs-volume-control, & .vjs-current-time, ' +
            '& .vjs-time-divider, & .vjs-duration']: {
                alignItems: 'center',
                height: '100%',
            },

            // fighting brightcove for specificity here
            ['&.not-hover.vjs-has-started.vjs-paused.vjs-user-active .vjs-control-bar, ' +
            '&.vjs-has-started.vjs-paused.vjs-user-inactive .vjs-control-bar, ' +
            '&.not-hover.vjs-has-started.vjs-playing.vjs-user-active .vjs-control-bar, ' +
            '&.vjs-has-started.vjs-playing.vjs-user-inactive .vjs-control-bar']:
                {
                    // overwrite to retain control bar until we want to dismiss it
                    transform: `translateY(0)`,
                },

            // override the lineheight on ie11
            '& .vjs-button > .vjs-icon-placeholder::before': {
                lineHeight: '2',
            },

            '& .vjs-control.vjs-button': {
                outline: 'none',
            },

            '& .vjs-control.vjs-volume-panel': {
                width: calcRem(spacing['3XL']),

                // &:has(.vjs-volume-bar.vjs-slider-horizontal:focus) -> Increase width / display when volume bar is focussed
                '&:hover, &:active, &:has(.vjs-volume-bar.vjs-slider-horizontal:focus)':
                    {
                        width: calcRem(92),
                    },

                [breakpoint('sm')]: {
                    '&:hover, .&:active': {
                        width: calcRem(92),
                    },
                },
            },

            ['& .vjs-control-bar button.vjs-play-control, ' +
            '& .vjs-control-bar button.vjs-mute-control']: {
                width: calcRem(spacing['3XL']),
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',

                [breakpoint('sm')]: {
                    width: calcRem(spacing['3XL']),
                },
            },

            ['& .vjs-play-control .vjs-icon-placeholder, ' +
            '& .vjs-mute-control  .vjs-icon-placeholder ']: {
                display: 'block',
                height: calcRem(spacing['S']),
                width: calcRem(spacing['3XL']),
                backgroundRepeat: 'no-repeat',
                backgroundSize: 'contain',

                '&:before': {
                    content: 'none',
                },

                [breakpoint('sm')]: {
                    height: calcRem(spacing['L']),
                    width: calcRem(spacing['L']),
                },
            },

            '& .vjs-play-control.vjs-paused .vjs-icon-placeholder': {
                backgroundImage: `url(${iconPlay})`,
            },

            '& .vjs-play-control.vjs-playing .vjs-icon-placeholder': {
                backgroundImage: `url(${iconPause})`,
            },

            '& .vjs-mute-control.vjs-vol-0 .vjs-icon-placeholder': {
                backgroundImage: `url(${iconVolumeX})`,
            },

            '& .vjs-mute-control.vjs-vol-1 .vjs-icon-placeholder': {
                backgroundImage: `url(${iconVolume0})`,
            },

            '& .vjs-mute-control.vjs-vol-2 .vjs-icon-placeholder': {
                backgroundImage: `url(${iconVolume1})`,
            },

            '& .vjs-mute-control.vjs-vol-3 .vjs-icon-placeholder': {
                backgroundImage: `url(${iconVolume2})`,
            },

            '& .vjs-fullscreen-control .vjs-icon-placeholder': {
                backgroundImage: `url(${iconMaximize})`,
            },

            '&.vjs-fullscreen .vjs-fullscreen-control .vjs-icon-placeholder': {
                backgroundImage: `url(${iconMinimize})`,
                marginLeft: 'auto',
                marginRight: 0,
            },

            '& .vjs-current-time.vjs-time-control': {
                marginLeft: calcRem(spacing['2XS']),
            },

            '& .vjs-fullscreen-control': {
                width: calcRem(spacing['3XL']),
            },

            '& .vjs-loading-spinner': {
                display: 'none',
            },

            '& .vjs-live-display': {
                fontSize: calcRem(15),
                padding: calcRem(7, 22),
                fontWeight: 500,
            },

            '& .vjs-fullscreen-control .vjs-icon-placeholder::before': {
                fontSize: calcRem(25),
            },

            '& .vjs-volume-menu-button-horizontal:hover': {
                '.vjs-menu .vjs-menu-content': {
                    width: calcRem(49),

                    [breakpoint('sm')]: {
                        width: calcRem(59),
                    },
                },
            },

            '& .vjs-volume-bar.vjs-slider-horizontal': {
                margin: `${calcRem(21)} 0 ${calcRem(20)}`,
                width: calcRem(49),
                height: calcRem(4),
                [breakpoint('sm')]: {
                    width: calcRem(59),
                },
            },
            '& .vjs-volume-bar.vjs-slider-horizontal:hover .vjs-menu': {
                width: calcRem(49),

                [breakpoint('sm')]: {
                    width: calcRem(59),
                },
            },

            '& .vjs-menu-button-inline': {
                '&:hover, &:focus': {
                    width: calcRem(94),

                    [breakpoint('sm')]: {
                        width: calcRem(104),
                    },
                },
            },

            '& .vjs-menu-button-inline .vjs-menu': {
                left: calcRem(45),
                width: calcRem(49),

                [breakpoint('sm')]: {
                    width: calcRem(59),
                },
            },

            '& .vjs-menu-button-inline.vjs-slider-active': {
                width: calcRem(94),

                [breakpoint('sm')]: {
                    width: calcRem(104),
                },
            },

            '& .vjs-slider-horizontal .vjs-volume-level': {
                height: calcRem(4),
            },

            '& .vjs-time-control': {
                lineHeight: calcRem(46),
                fontSize: calcRem(16),
                padding: 0,
                minWidth: calcRem(32),
                fontWeight: 700,
                color: text.inverse,
            },

            '& .vjs-time-control.vjs-time-divider': {
                minWidth: 0,
            },

            // See https://github.com/videojs/video.js/blob/master/docs/guides/text-tracks.md#text-track-settings & https://github.com/videojs/video.js/issues/3202
            '& .vjs-texttrack-settings': {
                display: 'none',
            },

            '& .vjs-play-progress, .vjs-volume-level': {
                background: fill.brand.strong,
            },

            '& .vjs-ad-control-bar .vjs-progress-control': {
                '& .vjs-play-progress.vjs-slider-bar': {
                    backgroundColor: '#FFE400',
                },
                '&:hover, &:active': {
                    height: calcRem(12),
                    top: calcRem(-12),
                },
            },
            '& .vjs-control.vjs-button:focus-visible, & .vjs-control:focus-visible .vjs-button':
                {
                    // Use default browser focus rings for Video player
                    outline: 'revert',
                    outlineOffset: calcRem(-2),
                    borderRadius: calcRem(metricsPN.radius['S']),
                },
        },

        {
            '& .vjs-progress-control': {
                height: calcRem(8),
                top: calcRem(-8),
            },

            '&.not-hover.vjs-has-started.vjs-paused.vjs-user-active .vjs-progress-control, &.not-hover.vjs-has-started.vjs-paused.vjs-user-inactive .vjs-progress-control, &.not-hover.vjs-has-started.vjs-playing.vjs-user-active .vjs-progress-control, &.not-hover.vjs-has-started.vjs-playing.vjs-user-inactive .vjs-progress-control, &.vjs-has-started.vjs-paused.vjs-ad-playing.vjs-user-inactive .vjs-progress-control, &.vjs-has-started.vjs-playing.vjs-user-inactive .vjs-progress-control':
                {
                    height: calcRem(8),
                    top: calcRem(-8),

                    '&:active': {
                        height: calcRem(12),
                    },
                },
        },
    ],

    /*
     * For Debugger / styling, comment out this block to pin control bar
     * to the bottom of the video element so you can style without hovering constantly
     */
    // Overwrite to dismiss video control bar.
    // Only dismiss if none of the controls are currently keyboard focussed.
    [`.${pnDismissControlClassName} &`]: {
        '.vjs-control-bar:not(:has(:focus-visible)), .vjs-ad-control-bar:not(:has(:focus-visible))':
            {
                // to dismiss the play control bar
                transform: `translateY(${calcRem(
                    panelHeightMobile - 1,
                )}) !important`,
                transition: 'transform 1s ease 0s',

                [breakpoint('sm')]: {
                    transform: `translateY(${calcRem(
                        panelHeightTablet - 1,
                    )}) !important`,
                },
                [breakpoint('lg')]: {
                    transform: `translateY(${calcRem(
                        panelHeightDesktop - 1,
                    )}) !important`,
                },

                '.vjs-progress-control:not(:has(:focus-visible))': {
                    top: calcRem(-4),
                },
            },

        '.vjs-progress-control:focus-within': {
            opacity: 1,
        },
    },
}))
